import React from 'react';
import { styled } from '@linaria/react';
import { toggle } from 'opticks';
import ContentWrapper from '@daedalus/atlas/helpers/ContentWrapper';
import { Grid } from '@daedalus/atlas/helpers/Grid';
import { Icon } from '@daedalus/atlas/Icon';
import { Price } from '@daedalus/atlas/Price';
import { Text } from '@daedalus/atlas/Text';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { getRefundType, RefundType } from '@daedalus/core/src/offer/business/cancellationPenalties';
import { CancellationPenalty } from '@daedalus/core/src/offer/types/offer';
import { NonRefundable } from './NonRefundable';
import { NonRefundablePeriod, PenaltyDetails } from './PenaltyDetails';
const NUMBER_OF_DECIMALS_FOR_PRICE = 2;
interface CancellationPenaltiesProps {
  /** A list of the cancellation penalties that are applied to the selected offer */
  cancellationPenalties: CancellationPenalty[];
  /** Total value of offer price */
  totalPrice: number;
  /** The display currency code of offer price */
  displayCurrencyCode: string;
  /**
   * The currency code of the chargeable price. This is used to show the disclaimer
   */
  chargeableCurrencyCode?: string;
  /** The hotel's time zone */
  hotelTimeZone?: string;
  /** If charge later refund policy should be sown */
  hasChargeLaterRefundPolicy?: boolean;
  /** If component rendered in popUp*/
  isInPopUp?: boolean;
  /** If the booking is a split booking */
  isSplitBookingDeal?: boolean;
  /** If this is after the booking has been made */
  isPostBooking?: boolean;
}
interface CancellationLabelProps {
  refundType: RefundType;
  totalPrice: number;
  currencyCode: string;
}
const _exp = /*#__PURE__*/() => Grid;
const LabelContainer = /*#__PURE__*/styled(_exp())({
  name: "LabelContainer",
  class: "l1l9n5v9",
  propsAsIs: true
});
const DisclaimerWrapper = /*#__PURE__*/styled('div')({
  name: "DisclaimerWrapper",
  class: "dwm7bhr",
  propsAsIs: false
});
const _exp5 = /*#__PURE__*/() => Text;
const DisclaimerIcon = /*#__PURE__*/styled(_exp5())({
  name: "DisclaimerIcon",
  class: "d15xndd6",
  propsAsIs: true
});
const DisclaimerInfoWrapper = /*#__PURE__*/styled('div')({
  name: "DisclaimerInfoWrapper",
  class: "d16qyhxg",
  propsAsIs: false
});
const CancellationPenaltiesLabels = ({
  refundType,
  totalPrice,
  currencyCode
}: CancellationLabelProps) => {
  if (refundType === RefundType.nonRefundable) {
    return <LabelContainer justify="space-between" direction="row" container spacing="s000" className="isNonRefundable">
        <Text variant="labelM">
          <FormattedMessageWrapper id="cancellationPenalties.nonRefundable" defaultMessage="Non-refundable" />
        </Text>
        <div data-id="nonRefundableTotalPrice">
          <Price maximumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE} minimumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE} value={totalPrice} currency={currencyCode} size="md" />
        </div>
      </LabelContainer>;
  }
  return <LabelContainer>
      <Text variant="labelM">
        <FormattedMessageWrapper id="cancellationPenalties.heading" defaultMessage="How much will it cost to cancel?" />
      </Text>
    </LabelContainer>;
};
export const CancellationPenalties = ({
  cancellationPenalties,
  totalPrice,
  displayCurrencyCode,
  chargeableCurrencyCode,
  hotelTimeZone,
  hasChargeLaterRefundPolicy,
  isInPopUp = false,
  isSplitBookingDeal,
  isPostBooking
}: CancellationPenaltiesProps) => {
  const isThereCurrencyConversion = !!chargeableCurrencyCode && !!displayCurrencyCode && displayCurrencyCode !== chargeableCurrencyCode;
  const refundType = getRefundType(cancellationPenalties);
  const isUpdatedPaymentTimeline = isPostBooking && toggle('3c85ed3b-update-payment-timeline-v3', false, true);
  const currencyStyle = isUpdatedPaymentTimeline ? 'code' : 'symbol';
  return <div data-id="cancellationPenaltiesComponent">
      <CancellationPenaltiesLabels refundType={refundType} totalPrice={totalPrice} currencyCode={displayCurrencyCode} />
      {refundType === RefundType.nonRefundable ? <NonRefundable isPostBooking={isPostBooking} /> : <>
          {cancellationPenalties.map((cancellationPenalty, index) => <PenaltyDetails key={cancellationPenalty.start} id={index} penalty={cancellationPenalty} totalOfferPrice={totalPrice} hotelTimeZone={hotelTimeZone} hasChargeLaterRefundPolicy={hasChargeLaterRefundPolicy} displayCurrencyCode={displayCurrencyCode} currencyStyle={currencyStyle} />)}
          <NonRefundablePeriod date={cancellationPenalties[cancellationPenalties.length - 1].end} totalOfferPrice={totalPrice} currency={cancellationPenalties[0].currency} displayCurrencyCode={displayCurrencyCode} hotelTimeZone={hotelTimeZone} currencyStyle={currencyStyle} />
        </>}

      <ContentWrapper paddingTop="s300">
        {!isUpdatedPaymentTimeline && <DisclaimerWrapper>
            {refundType !== RefundType.nonRefundable && <DisclaimerIcon>
                <Icon name="Information" colorPath="content.neutral.c600" size="sm" />
              </DisclaimerIcon>}

            <div>
              {!isSplitBookingDeal && !isPostBooking && !(hasChargeLaterRefundPolicy && isInPopUp) && <DisclaimerInfoWrapper>
                    <Text variant="bodyS" colorPath="content.neutral.c600">
                      <FormattedMessageWrapper id="cancellationPenalties.propertyDisclaimerSingleRoom" defaultMessage="Cancellation costs based on the total booking value." />
                    </Text>
                  </DisclaimerInfoWrapper>}
              {refundType !== RefundType.nonRefundable && <DisclaimerInfoWrapper>
                  <Text variant="bodyS" colorPath="content.neutral.c600">
                    <FormattedMessageWrapper id="cancellationPenalties.propertyTimezone" defaultMessage="Dates and times refer to property’s local time." />
                  </Text>
                </DisclaimerInfoWrapper>}
            </div>
          </DisclaimerWrapper>}

        {isUpdatedPaymentTimeline && <>
            <DisclaimerWrapper>
              <DisclaimerIcon>
                <Icon name="Information" colorPath="content.neutral.c600" size="sm" />
              </DisclaimerIcon>
              <div>
                <DisclaimerInfoWrapper>
                  <Text variant="bodyS" colorPath="content.neutral.c600">
                    <FormattedMessageWrapper id="cancellationPenalties.propertyDisclaimerSingleRoom" defaultMessage="Cancellation costs based on the total booking value." />{' '}
                    <FormattedMessageWrapper id="cancellationPenalties.propertyTimezone" defaultMessage="Dates and times refer to property’s local time." />
                  </Text>
                </DisclaimerInfoWrapper>
                {isThereCurrencyConversion && <DisclaimerInfoWrapper>
                    <Text variant="bodyS" colorPath="content.neutral.c600">
                      <FormattedMessageWrapper id="cancellationPenalties.penaltyConversionDisclaimer" defaultMessage="{displayCurrencyCode} price shown is approximate based on the exchange rate of charged currency ({chargeableCurrencyCode}) at the time of booking." values={{
                  displayCurrencyCode,
                  chargeableCurrencyCode
                }} />
                    </Text>
                  </DisclaimerInfoWrapper>}
              </div>
            </DisclaimerWrapper>
          </>}
      </ContentWrapper>
    </div>;
};

require("./CancellationPenalties.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./CancellationPenalties.tsx");